import { OrganizationRole } from 'src/context/types';
import { RoleGated } from '../types';

export interface RouteEntry extends RoleGated {
  id: string;
  /** i18n key under `globalSearch.entries`. */
  labelKey: string;
  /** Leaf path passed to buildPath(); never starts with a slash. */
  path: string;
}

const ALL: OrganizationRole[] = ['owner', 'admin', 'member'];
const MANAGERS: OrganizationRole[] = ['owner', 'admin'];
// Mirrors `canManageRestaurantSettings` (see `useAuthPermissions`), which is `isOwner ||
// isPlatformAdmin` — org admins are deliberately excluded, unlike `canManageOrg`/MANAGERS.
const RESTAURANT_SETTINGS_MANAGERS: OrganizationRole[] = ['owner'];

/**
 * Roles mirror each page's backing controller `@RequireOrgRole`, not MainSideNav.
 * See Ruling 1 in the plan: the nav is stricter for menu/restaurant settings and
 * looser for dashboard, both pre-existing and deliberately not changed here.
 */
export const ROUTE_INDEX: RouteEntry[] = [
  { id: 'dashboard.selling', labelKey: 'dashboardSelling', path: 'dashboard/selling', roles: MANAGERS },
  { id: 'dashboard.accounting', labelKey: 'dashboardAccounting', path: 'dashboard/accounting', roles: MANAGERS },
  { id: 'dashboard.inventory', labelKey: 'dashboardInventory', path: 'dashboard/inventory', roles: MANAGERS },

  { id: 'pos', labelKey: 'pos', path: 'pos', roles: ALL },
  { id: 'kitchen', labelKey: 'kitchen', path: 'kitchen', roles: ALL },
  { id: 'delivery', labelKey: 'delivery', path: 'delivery', roles: ALL },

  { id: 'orders.today', labelKey: 'ordersToday', path: 'orders/today', roles: ALL },
  { id: 'orders.all', labelKey: 'ordersAll', path: 'orders/all', roles: ALL },

  { id: 'accounting.transactions', labelKey: 'transactions', path: 'accounting/transactions', roles: MANAGERS },
  { id: 'accounting.expenses', labelKey: 'expenses', path: 'accounting/expenses', roles: MANAGERS },
  { id: 'accounting.incomes', labelKey: 'incomes', path: 'accounting/incomes', roles: MANAGERS },
  { id: 'accounting.investments', labelKey: 'investments', path: 'accounting/investments', roles: MANAGERS },
  {
    id: 'accounting.settings',
    labelKey: 'transactionCategories',
    path: 'settings/accounting/categories',
    roles: RESTAURANT_SETTINGS_MANAGERS,
  },

  { id: 'inventory.items', labelKey: 'inventoryItems', path: 'inventory/items', roles: MANAGERS },
  { id: 'inventory.movements', labelKey: 'inventoryMovements', path: 'inventory/movements', roles: MANAGERS },
  { id: 'inventory.categories', labelKey: 'inventoryCategories', path: 'inventory/categories', roles: MANAGERS },

  { id: 'employees.all', labelKey: 'employees', path: 'employees/all', roles: MANAGERS },
  {
    id: 'employees.rules',
    labelKey: 'employeeRules',
    path: 'settings/employees/rules',
    roles: RESTAURANT_SETTINGS_MANAGERS,
  },
  {
    id: 'employees.dayOff',
    labelKey: 'employeeDayOff',
    path: 'settings/employees/day-off',
    roles: RESTAURANT_SETTINGS_MANAGERS,
  },
  { id: 'employees.dashboard', labelKey: 'employeeDashboard', path: 'employees/dashboard', roles: ALL },
  { id: 'timesheets.calendar', labelKey: 'timesheetCalendar', path: 'timesheets/calendar', roles: MANAGERS },
  { id: 'me.calendar', labelKey: 'myCalendar', path: 'me/calendar', roles: ALL },

  { id: 'menu.main', labelKey: 'menuSettings', path: 'settings/menu', roles: RESTAURANT_SETTINGS_MANAGERS },
  { id: 'menu.items', labelKey: 'menuItems', path: 'settings/menu/items', roles: RESTAURANT_SETTINGS_MANAGERS },
  {
    id: 'menu.variants',
    labelKey: 'menuItemVariants',
    path: 'settings/menu/item-variants',
    roles: RESTAURANT_SETTINGS_MANAGERS,
  },

  {
    id: 'restaurant.info',
    labelKey: 'restaurantSettings',
    path: 'settings/restaurant',
    roles: RESTAURANT_SETTINGS_MANAGERS,
  },
  {
    id: 'restaurant.branches',
    labelKey: 'branches',
    path: 'settings/restaurant/branches',
    roles: RESTAURANT_SETTINGS_MANAGERS,
  },
  {
    id: 'restaurant.tables',
    labelKey: 'tables',
    path: 'settings/restaurant/tables',
    roles: RESTAURANT_SETTINGS_MANAGERS,
  },

  { id: 'org.general', labelKey: 'orgGeneral', path: 'settings/organization', roles: MANAGERS },
  { id: 'org.members', labelKey: 'orgMembers', path: 'settings/organization/members', roles: MANAGERS },
  { id: 'org.invitations', labelKey: 'orgInvitations', path: 'settings/organization/invitations', roles: MANAGERS },
  { id: 'org.audit', labelKey: 'orgAuditLog', path: 'settings/organization/audit', roles: MANAGERS },

  { id: 'profile', labelKey: 'profile', path: 'profile', roles: ALL },
];

export function visibleRoutes(role: OrganizationRole | null, isPlatformAdmin: boolean): RouteEntry[] {
  if (isPlatformAdmin) return ROUTE_INDEX;
  if (!role) return [];
  return ROUTE_INDEX.filter((entry) => entry.roles.includes(role));
}
